import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import AuthModal from '../components/AuthModal';
import { useProject } from '../contexts/ProjectContext';
import { buildBg } from '../components/landing/bgHelpers';
import { BACKEND_BASE } from '../services/api';

/**
 * Página de registro independiente.
 *
 * El proyecto la activa desde el admin (Login y registro → "Página de registro
 * independiente") y elige la URL. El formulario es el MISMO `AuthModal` en modo
 * `embedded`, así que campos, validaciones y checks no se duplican; acá se
 * configura la *presentación*: fondo, bloque de texto, y un puñado de overrides
 * de estilo para la tarjeta (que no tocan el modal de la landing).
 *
 * Tres disposiciones:
 *   - `centrado`   (por defecto): solo el formulario. Es lo que había.
 *   - `encabezado`: logo, título, aval, texto legal y código centrados ARRIBA,
 *                   y el formulario debajo.
 *   - `split`:      el mismo bloque a la izquierda y el formulario a la derecha.
 */

const MQ_COMPACT = '@media (max-width: 900px)';
const MQ_MOBILE = '@media (max-width: 600px)';

const Section = styled.section`
  position: relative;
  overflow: hidden;
  min-height: 100%;
  background: ${p => p.$bg || 'transparent'};
  ${p => p.$img && `
    background-image: ${p.$overlay ? `linear-gradient(${p.$overlay}, ${p.$overlay}), ` : ''}url(${p.$img});
    background-size: ${p.$bgSize || 'cover'};
    background-position: ${p.$bgPos || 'center'};
    background-repeat: ${p.$bgRepeat || 'no-repeat'};
    background-attachment: ${p.$bgAttach || 'scroll'};
  `}
  padding: ${p => p.$padY || '60px'} 20px;

  ${MQ_COMPACT} {
    padding: 40px 16px;
    /* iOS no soporta background-attachment: fixed; en móvil siempre scroll. */
    background-attachment: scroll;
  }
`;

const Container = styled.div`
  max-width: ${p => p.$maxWidth || '1180px'};
  margin: 0 auto;
  display: grid;
  grid-template-columns: ${p => p.$split ? '1fr 1fr' : '1fr'};
  gap: ${p => p.$gap || '48px'};
  align-items: center;

  ${MQ_COMPACT} {
    grid-template-columns: 1fr;
    gap: 28px;
  }
`;

const Columna = styled.div`
  color: ${p => p.$color || '#ffffff'};
  text-align: ${p => p.$align || 'left'};
  ${p => p.$maxWidth && `max-width: ${p.$maxWidth}; margin: 0 auto;`}

  ${MQ_COMPACT} { text-align: center; }
`;

const Logo = styled.img`
  display: block;
  width: ${p => p.$ancho || '260px'};
  max-width: 100%;
  height: auto;
  margin-bottom: 18px;
  ${p => p.$align === 'center' && 'margin-left: auto; margin-right: auto;'}

  ${MQ_COMPACT} { margin-left: auto; margin-right: auto; }
`;

const Titulo = styled.h1`
  margin: 0 0 18px;
  font-size: ${p => p.$size || '40px'};
  line-height: 1.15;
  font-weight: ${p => p.$weight || '400'};
  color: inherit;

  strong, b {
    font-size: ${p => p.$strongSize || p.$size || '40px'};
    font-weight: ${p => p.$strongWeight || '700'};
    display: ${p => p.$strongBlock ? 'block' : 'inline'};
  }

  ${MQ_MOBILE} {
    font-size: ${p => p.$sizeMobile || '30px'};
    strong, b { font-size: ${p => p.$strongSizeMobile || p.$sizeMobile || '30px'}; }
  }
`;

// Fila "AVALADO POR: [logo]" — el rótulo es opcional.
const AvalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${p => p.$align === 'center' ? 'center' : 'flex-start'};
  gap: 12px;
  margin-bottom: 18px;

  span {
    font-size: ${p => p.$labelSize || '11px'};
    letter-spacing: 0.08em;
    text-transform: uppercase;
    opacity: 0.85;
  }
  img {
    width: ${p => p.$ancho || '180px'};
    max-width: 100%;
    height: auto;
  }

  ${MQ_COMPACT} { justify-content: center; }
`;

const Texto = styled.div`
  font-size: ${p => p.$size || '13px'};
  line-height: 1.6;
  opacity: ${p => p.$opacidad || 0.9};

  p { margin: 0 0 10px; }
  a { color: inherit; text-decoration: underline; }
`;

const Codigo = styled.div`
  margin-top: 10px;
  font-size: ${p => p.$size || '12px'};
  letter-spacing: 0.04em;
  opacity: 0.75;
`;

const TituloForm = styled.h2`
  margin: 0 0 14px;
  text-align: center;
  font-size: ${p => p.$size || '24px'};
  font-weight: 700;
  color: ${p => p.$color || '#ffffff'};
`;

const EnlaceInferior = styled.div`
  text-align: center;
  margin-top: 18px;
  font-size: ${p => p.$size || '14px'};

  a {
    color: ${p => p.$color || '#7fd3f5'};
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

// Logo suelto anclado a una esquina (el de la marca/patrocinador). Va fuera del
// flujo, así que no empuja el formulario. La "placa" de fondo es opcional: sin
// color ni relleno queda solo la imagen, como antes.
const LogoEsquina = styled.div`
  position: ${p => p.$fijo ? 'fixed' : 'absolute'};
  ${p => (p.$pos || 'bottom-right').includes('bottom')
    ? `bottom: ${p.$offsetY || '24px'};`
    : `top: ${p.$offsetY || '24px'};`}
  ${p => (p.$pos || 'bottom-right').includes('right')
    ? `right: ${p.$offsetX || '24px'};`
    : `left: ${p.$offsetX || '24px'};`}
  z-index: 2;
  pointer-events: none;
  opacity: ${p => p.$opacidad || 1};
  ${p => p.$fondo && `background: ${p.$fondo};`}
  ${p => p.$padding && `padding: ${p.$padding};`}
  ${p => p.$radio && `border-radius: ${p.$radio};`}
  line-height: 0;

  img {
    width: ${p => p.$ancho || '240px'};
    max-width: 40vw;
    height: auto;
    display: block;
  }

  ${MQ_COMPACT} {
    position: static;
    display: ${p => p.$ocultarMovil ? 'none' : 'block'};
    width: fit-content;
    margin: 28px auto 0;
    ${p => p.$radio && 'border-radius: 14px;'}

    img {
      width: ${p => p.$anchoMovil || '180px'};
      max-width: 70vw;
    }
  }
`;

const url = (v) => (!v ? null : (v.startsWith('http') ? v : `${BACKEND_BASE}/${v}`));

const RegistroStandalone = ({ mostrarLogin = true }) => {
  const navigate = useNavigate();
  const { proyecto } = useProject();
  const cfg = proyecto?.config_json?.registro_config?.pagina_registro || {};

  const layout = cfg.layout || 'centrado';
  const split = layout === 'split';
  const conTexto = split || layout === 'encabezado';
  const align = split ? (cfg.alineacion || 'left') : 'center';

  const logo = url(cfg.logo_url);
  const logoAval = url(cfg.logo_aval_url);

  // Overrides de estilo SOLO para esta página: el modal de la landing no cambia.
  const overrideCard = {};
  const mapear = {
    card_fondo: 'color_fondo_modal',
    card_color_texto: 'color_texto',
    card_color_texto_secundario: 'color_texto_secundario',
    card_ancho: 'ancho_modal',
    card_radio: 'radio_modal',
    card_borde: 'borde_modal',
    card_sombra: 'sombra_modal',
    card_blur: 'blur_modal',
    card_fondo_input: 'color_fondo_input',
    card_borde_input: 'color_borde_input',
    card_radio_input: 'radio_inputs',
    card_color_primario: 'color_primario',
    card_color_secundario: 'color_secundario',
  };
  Object.entries(mapear).forEach(([desde, hacia]) => {
    if (cfg[desde]) overrideCard[hacia] = cfg[desde];
  });
  if (cfg.card_ocultar_encabezado) overrideCard.ocultar_encabezado = true;
  if (cfg.card_ocultar_placeholders) overrideCard.ocultar_placeholders = true;

  const formulario = (
    <div>
      {cfg.titulo_formulario && (
        <TituloForm $size={cfg.tamano_titulo_formulario} $color={cfg.color_titulo_formulario || cfg.color_texto}>
          {cfg.titulo_formulario}
        </TituloForm>
      )}
      <AuthModal
        isOpen
        embedded
        initialTab="register"
        showLoginTab={mostrarLogin}
        onClose={() => navigate('/')}
        configOverride={Object.keys(overrideCard).length ? overrideCard : null}
      />
      {cfg.enlace_texto && (
        <EnlaceInferior $color={cfg.color_enlace} $size={cfg.tamano_enlace}>
          <a href={cfg.enlace_url || '#'}>{cfg.enlace_texto}</a>
        </EnlaceInferior>
      )}
    </div>
  );

  const bloqueTexto = conTexto ? (
    <Columna $color={cfg.color_texto} $align={align} $maxWidth={split ? null : cfg.ancho_texto}>
      {logo && <Logo src={logo} alt="" $ancho={cfg.logo_ancho} $align={align} />}
      {cfg.titulo && (
        <Titulo
          $size={cfg.tamano_titulo}
          $sizeMobile={cfg.tamano_titulo_movil}
          $weight={cfg.peso_titulo}
          $strongSize={cfg.tamano_destacado}
          $strongSizeMobile={cfg.tamano_destacado_movil}
          $strongWeight={cfg.peso_destacado}
          $strongBlock={cfg.destacado_en_linea_aparte !== false}
          dangerouslySetInnerHTML={{ __html: cfg.titulo }}
        />
      )}
      {logoAval && (
        <AvalRow $align={align} $ancho={cfg.logo_aval_ancho} $labelSize={cfg.tamano_aval_label}>
          {cfg.texto_aval && <span>{cfg.texto_aval}</span>}
          <img src={logoAval} alt="" />
        </AvalRow>
      )}
      {cfg.texto && (
        <Texto
          $size={cfg.tamano_texto}
          $opacidad={cfg.opacidad_texto}
          dangerouslySetInnerHTML={{ __html: cfg.texto }}
        />
      )}
      {cfg.codigo && <Codigo $size={cfg.tamano_codigo}>{cfg.codigo}</Codigo>}
    </Columna>
  ) : null;

  return (
    <Section
      $bg={buildBg(cfg, 'transparent')}
      $img={url(cfg.imagen_fondo)}
      $overlay={cfg.overlay_fondo}
      $bgSize={cfg.tamano_fondo}
      $bgPos={cfg.posicion_fondo}
      $bgRepeat={cfg.repeat_fondo}
      $bgAttach={cfg.attach_fondo}
      $padY={cfg.espacio_vertical}
    >
      <Container $split={split} $maxWidth={cfg.ancho_max} $gap={cfg.separacion_columnas}>
        {bloqueTexto}
        {formulario}
      </Container>
      {url(cfg.logo_esquina_url) && (
        <LogoEsquina
          $pos={cfg.logo_esquina_pos}
          $fondo={cfg.logo_esquina_fondo}
          $padding={cfg.logo_esquina_padding}
          $radio={cfg.logo_esquina_radio}
          $ancho={cfg.logo_esquina_ancho}
          $anchoMovil={cfg.logo_esquina_ancho_movil}
          $offsetX={cfg.logo_esquina_offset_x}
          $offsetY={cfg.logo_esquina_offset_y}
          $fijo={cfg.logo_esquina_fijo}
          $opacidad={cfg.logo_esquina_opacidad}
          $ocultarMovil={cfg.logo_esquina_ocultar_movil}
        >
          <img src={url(cfg.logo_esquina_url)} alt="" />
        </LogoEsquina>
      )}
    </Section>
  );
};

export default RegistroStandalone;
